import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { CheckCircle2, ChevronsUp, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

/**
 * Reads the recovery params from the URL once, at mount. The email sends a
 * `token_hash` link (?token_hash=...&type=recovery) to THIS page rather than
 * Supabase's single-use /auth/v1/verify URL — so a mail scanner's pre-fetch
 * (M365 Defender Safe Links) only loads the SPA; the one-time token is consumed
 * exactly once, when the user submits and we call verifyOtp(). GoTrue error
 * redirects (?error=...&error_description=...) are read too, so an expired link
 * shows a clear message instead of a dead form.
 */
function readRecoveryParams() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return {
    tokenHash: search.get("token_hash") ?? hash.get("token_hash"),
    type: search.get("type") ?? hash.get("type"),
    errorDescription: search.get("error_description") ?? hash.get("error_description"),
  };
}

function clearRecoveryParamsFromUrl() {
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));

  url.searchParams.delete("token_hash");
  url.searchParams.delete("type");
  hash.delete("token_hash");
  hash.delete("type");
  url.hash = hash.toString() ? `#${hash.toString()}` : "";

  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export default function ResetPassword() {
  const { session, isLoading, signOut } = useAuth();
  const [recovery] = useState(readRecoveryParams);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(
    recovery.errorDescription ? decodeURIComponent(recovery.errorDescription) : null,
  );
  // Once verifyOtp consumes the single-use token it leaves a recovery session
  // behind. If updateUser then fails (policy rejection, same-as-current
  // password, transient error), the user must be able to retry WITHOUT
  // re-verifying — the token is already spent, so a second verifyOtp would fail
  // as "expired". Track success and skip straight to updateUser on retries.
  const [verified, setVerified] = useState(false);
  const [success, setSuccess] = useState(false);

  // GoTrue already rejected the link (expired/used) and redirected back with an
  // error — there's no token left to redeem, so don't offer the form.
  const linkRejected = Boolean(recovery.errorDescription);
  // We can set a new password if we have a token_hash to redeem now, or an
  // already-hydrated recovery session (admin links via /auth/callback).
  const canReset = !linkRejected && (Boolean(recovery.tokenHash) || Boolean(session));

  if (success) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMsg(null);

    if (password.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      // Redeem the one-time token here, on the user's explicit submit. This is
      // what consumes it — so a link-scanner that merely GET the page can't burn
      // it first. verifyOtp is a direct API call (no redirect, no PKCE verifier),
      // so it works from any device/email client. Skip on retries: the token is
      // single-use, but a successful verify already left us a recovery session.
      if (recovery.tokenHash && !verified) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: (recovery.type as "recovery") || "recovery",
          token_hash: recovery.tokenHash,
        });
        if (verifyError) {
          setErrorMsg(
            "This reset link is invalid or has expired. Request a new one from the sign-in page.",
          );
          return;
        }
        setVerified(true);
        // Keep retries on the session path instead of leaving the now-consumed
        // token in the address bar. A refresh after a policy/update failure can
        // still reset via the recovery session created by verifyOtp().
        clearRecoveryParamsFromUrl();
      }

      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setErrorMsg(error.message);
        return;
      }

      await signOut();
      setSuccess(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background px-6 py-12">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-coral/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-32 h-96 w-96 rounded-full bg-gold/15 blur-3xl"
      />

      <div className="relative w-full max-w-sm space-y-7">
        <div className="flex animate-rise flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-coral shadow-glow">
            <ChevronsUp className="h-8 w-8 text-white" strokeWidth={2.75} />
          </span>
          <h1 className="mt-4 font-display text-4xl">
            <span className="text-gradient-coral">Relay</span>
          </h1>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            by Inspired Edge Group
          </p>
        </div>

        <Card className="animate-rise shadow-elevated" style={{ animationDelay: "80ms" }}>
          <CardHeader>
            <CardTitle className="text-base">Set a new password</CardTitle>
            <CardDescription>
              Enter a new password for your Relay account. After saving, sign in again with your
              new password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {linkRejected ? (
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  This reset link is invalid or has expired. Head back to the sign-in page and
                  choose <strong>Forgot password?</strong> to request a fresh link, then open the
                  newest email.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Back to sign in</Link>
                </Button>
              </div>
            ) : !recovery.tokenHash && isLoading ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking your reset session…
              </p>
            ) : !canReset ? (
              <div className="space-y-4 text-sm text-muted-foreground">
                <p>
                  Open this page from the password-reset link we emailed you. If you don't have one,
                  go to the sign-in page and choose <strong>Forgot password?</strong> to request it.
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Back to sign in</Link>
                </Button>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <Label htmlFor="new-password" className="mb-1.5 block">
                    New password
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="confirm-password" className="mb-1.5 block">
                    Confirm password
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                {errorMsg && (
                  <p className="text-sm text-destructive" role="alert">
                    {errorMsg}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="gradient"
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Save new password
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
